import {FC, useState, useCallback, useEffect, useRef} from "react";
import {useTranslation} from "react-i18next";
import {TextInput} from "@interstate/components/TextInput";
import {SelectInput} from "@interstate/components/SelectInput";
import {RadioButtonList} from "@interstate/components/RadioButtonList";
import type {RadioButtonEventValue} from "@interstate/components/RadioButtonList";
import {DealerStatus} from "../../model";
import {useCreateNewEntity} from "./stepper/context";
import {useTenantValidation} from "./hooks/useTenantValidation";
import "./CreateNewEntityForm.scss";

// Constants
const MAX_FIELD_LENGTH = 120;

interface FormData {
  tenantChoice: "new" | "existing" | "";
  tenantNewName: string;
  tenantExistingId: string;
  dbaName: string;
  legalName: string;
  status: string;
  telecomNumbers: string;
  streetAddressLine1: string;
  streetAddressLine2: string;
  city: string;
  state: string;
  country: string;
  zip: string;
}

export type {FormData as CreateNewEntityFormData};

interface FormErrors {
  tenantChoice?: string;
  tenantNewName?: string;
  tenantExistingId?: string;
  dbaName?: string;
  legalName?: string;
  status?: string;
  telecomNumbers?: string;
  streetAddressLine1?: string;
  streetAddressLine2?: string;
  city?: string;
  state?: string;
  country?: string;
  zip?: string;
  general?: string;
}

interface FormState {
  data: FormData;
  errors: FormErrors;
  touched: Record<keyof FormData, boolean>;
  isValid: boolean;
  hasSubmitAttempted: boolean;
}

interface TestEntityFormProps {
  onCancel?: () => void;
  disabled?: boolean;
  onFormDataChange?: (data: FormData) => void;
  onValidationChange?: (isValid: boolean) => void;
  onTenantValidated?: (tenantName: string | null) => void;
}

/**
 * Form component for creating new operational entities.
 * Uses Interstate UI components with left-aligned embedded layout.
 * Includes comprehensive form state management, validation, and error handling.
 * Integrates with CreateNewEntity context for data management.
 */
export const CreateNewEntityForm: FC<TestEntityFormProps> = ({
  onCancel,
  disabled = false,
  onFormDataChange,
  onValidationChange,
  onTenantValidated
}) => {
  const {t} = useTranslation();
  const controller = useCreateNewEntity();
  const {isValidating, validatedTenant, error: tenantValidationError, validateTenantId, clearValidation} = useTenantValidation();

  // Notify parent when tenant validation resolves
  useEffect(() => {
    onTenantValidated?.(validatedTenant?.name ?? null);
  }, [validatedTenant, onTenantValidated]);

  // Track whether the user has manually edited the tenant new name field
  const tenantNewNameManuallyEdited = useRef(false);

  const [formState, setFormState] = useState<FormState>({
    data: {
      ...controller.formData
    },
    errors: {},
    touched: {
      tenantChoice: false,
      tenantNewName: false,
      tenantExistingId: false,
      dbaName: false,
      legalName: false,
      status: false,
      telecomNumbers: false,
      streetAddressLine1: false,
      streetAddressLine2: false,
      city: false,
      state: false,
      country: false,
      zip: false
    },
    isValid: false,
    hasSubmitAttempted: false
  });

  // Validation rules
  const validateNameField = useCallback(
    (
      value: string,
      fieldName: string,
      isRequired = false
    ): string | undefined => {
      const trimmedValue = value.trim();

      if (isRequired && !trimmedValue) {
        return t(
          `form.error.${fieldName}-required`,
          `${fieldName} is required`
        );
      }

      if (trimmedValue.length > MAX_FIELD_LENGTH) {
        return t(
          `form.error.${fieldName}-too-long`,
          `${fieldName} must be less than ${MAX_FIELD_LENGTH} characters`
        );
      }
      return undefined;
    },
    [t]
  );

  const validateTelecomNumbers = useCallback(
    (value: string): string | undefined => {
      if (value.trim()) {
        const numbers = value
          .split(",")
          .map(n => n.trim())
          .filter(n => n.length > 0);
        for (const number of numbers) {
          // Basic phone number validation (allows various formats)
          if (!/^[\d\s\-()+=]{7,20}$/.test(number)) {
            return t(
              "form.error.invalid-phone-format",
              "Invalid phone number format. Use digits, spaces, hyphens, parentheses, or plus signs"
            );
          }
        }
      }
      return undefined;
    },
    [t]
  );

  const validateZip = useCallback(
    (value: string): string | undefined => {
      if (value.trim() && !/^[\w\s-]{3,10}$/.test(value.trim())) {
        return t("form.error.invalid-zip-format", "Invalid ZIP code format");
      }
      return undefined;
    },
    [t]
  );

  const validateGeneralField = useCallback(
    (value: string): string | undefined => {
      if (value.trim().length > MAX_FIELD_LENGTH) {
        return t(
          "form.error.field-too-long",
          `Field must be less than ${MAX_FIELD_LENGTH} characters`
        );
      }
      return undefined;
    },
    [t]
  );

  const validateField = useCallback(
    (field: keyof FormData, value: string, currentData?: FormData): string | undefined => {
      const data = currentData ?? formState.data;
      switch (field) {
        case "tenantChoice":
          return !value
            ? t("form.error.tenant-choice-required", "Please select a tenant option")
            : undefined;
        case "tenantNewName":
          if (data.tenantChoice === "new") {
            return !value.trim()
              ? t("form.error.tenant-new-name-required", "Tenant name is required")
              : validateNameField(value, "tenant-new-name", false);
          }
          return undefined;
        case "tenantExistingId":
          if (data.tenantChoice === "existing") {
            return !value.trim()
              ? t("form.error.tenant-existing-id-required", "Tenant ID is required")
              : undefined;
          }
          return undefined;
        case "dbaName":
          return validateNameField(value, "dba-name", true);
        case "legalName":
          return validateNameField(value, "legal-name", false);
        case "telecomNumbers":
          return validateTelecomNumbers(value);
        case "zip":
          return validateZip(value);
        default:
          return validateGeneralField(value);
      }
    },
    [
      formState.data,
      validateNameField,
      validateTelecomNumbers,
      validateZip,
      validateGeneralField,
      t
    ]
  );

  // Validate entire form
  const validateForm = useCallback(
    (data: FormData): FormErrors => {
      const errors: FormErrors = {};

      Object.keys(data).forEach(key => {
        const field = key as keyof FormData;
        const error = validateField(field, data[field], data);
        if (error) {
          errors[field] = error;
        }
      });

      return errors;
    },
    [validateField]
  );

  // Update form validity when data or errors change
  useEffect(() => {
    const errors = validateForm(formState.data);
    const isValid = Object.keys(errors).length === 0;

    setFormState(prev => ({
      ...prev,
      errors,
      isValid
    }));

    // For "existing" tenant, also require successful backend validation
    const tenantValid = formState.data.tenantChoice === "existing"
      ? !!validatedTenant && !tenantValidationError && !isValidating
      : true;
    const combinedValid = isValid && tenantValid;

    // Notify context of validation state change
    controller.onValidationChange(combinedValid);
    onValidationChange?.(combinedValid);

    // Update form data in context
    controller.onFormDataChange(formState.data);
    onFormDataChange?.(formState.data);
  }, [formState.data, validateForm, controller, validatedTenant, tenantValidationError, isValidating]);

  // Sync tenantNewName with dbaName when choice is "new" and user hasn't manually edited it
  useEffect(() => {
    if (
      formState.data.tenantChoice === "new" &&
      !tenantNewNameManuallyEdited.current
    ) {
      setFormState(prev => ({
        ...prev,
        data: {
          ...prev.data,
          tenantNewName: prev.data.dbaName
        }
      }));
    }
  }, [formState.data.dbaName, formState.data.tenantChoice]);

  // Handle field changes
  const handleInputChange = useCallback(
    (field: keyof FormData) => (event: any) => {
      const value = event.target.value;

      if (field === "tenantNewName") {
        tenantNewNameManuallyEdited.current = true;
      }

      setFormState(prev => ({
        ...prev,
        data: {
          ...prev.data,
          [field]: value
        },
        touched: {
          ...prev.touched,
          [field]: true
        }
      }));
    },
    []
  );

  const handleTenantChoiceChange = useCallback(
    (event: {target: {value: RadioButtonEventValue}}) => {
      const value = event.target.value as "new" | "existing" | "";

      // When switching to "new", reset the manual edit flag so dbaName syncs again
      if (value === "new") {
        tenantNewNameManuallyEdited.current = false;
      }

      setFormState(prev => ({
        ...prev,
        data: {
          ...prev.data,
          tenantChoice: value,
          // Pre-populate tenantNewName with dbaName when switching to "new"
          ...(value === "new" && {tenantNewName: prev.data.dbaName})
        },
        touched: {
          ...prev.touched,
          tenantChoice: true
        }
      }));
    },
    []
  );

  const handleSubmit = useCallback(async () => {
    setFormState(prev => ({
      ...prev,
      hasSubmitAttempted: true
    }));

    if (formState.isValid) {
      await controller.onFormSubmit();
    }
  }, [formState.isValid, controller]);

  const handleKeyDown = useCallback(
    (event: any) => {
      if (event.key === "Enter" && !controller.isSubmitting && !disabled) {
        event.preventDefault();
        handleSubmit();
      }
    },
    [controller.isSubmitting, disabled, handleSubmit]
  );

  // Helper function to determine if field error should be shown
  const shouldShowError = (field: keyof FormData): boolean => {
    return (
      (formState.touched[field] || formState.hasSubmitAttempted) &&
      !!formState.errors[field]
    );
  };

  const isFormDisabled = disabled || controller.isSubmitting;

  return (
    <div className="create-new-entity-form">
      <div className="form-section">
        <h3 className="section-title">
          {t("create-new-entity.tenant-selection", "Tenant Selection")}
        </h3>

        <div className="form-row">
          <RadioButtonList
            id="tenant-choice-radio"
            data-testid="tenant-choice-radio"
            name="tenantChoice"
            label={t("create-new-entity.tenant-choice-label", "Tenant")}
            value={formState.data.tenantChoice}
            onChange={handleTenantChoiceChange}
            hasError={shouldShowError("tenantChoice")}
            errorMessage={formState.errors.tenantChoice}
            options={[
              {
                value: "new",
                label: t("create-new-entity.tenant-choice-new", "Create new tenant"),
                disabled: isFormDisabled
              },
              {
                value: "existing",
                label: t("create-new-entity.tenant-choice-existing", "Use existing tenant"),
                disabled: isFormDisabled
              }
            ]}
            required
          />
        </div>

        {formState.data.tenantChoice === "new" && (
          <div className="form-row">
            <TextInput
              id="tenant-new-name-input"
              data-testid="tenant-new-name-input"
              name="tenantNewName"
              label={t("create-new-entity.tenant-new-name", "Tenant Name")}
              value={formState.data.tenantNewName}
              onChange={handleInputChange("tenantNewName")}
              onKeyDown={handleKeyDown}
              hasError={shouldShowError("tenantNewName")}
              errorMessage={formState.errors.tenantNewName}
              disabled={isFormDisabled}
              required
            />
          </div>
        )}

        {formState.data.tenantChoice === "existing" && (
          <div className="form-row">
            <TextInput
              id="tenant-existing-id-input"
              data-testid="tenant-existing-id-input"
              name="tenantExistingId"
              label={t("create-new-entity.tenant-existing-id", "Tenant ID")}
              value={formState.data.tenantExistingId}
              onChange={(event: any) => {
                handleInputChange("tenantExistingId")(event);
                clearValidation();
              }}
              onBlur={() => validateTenantId(formState.data.tenantExistingId)}
              onKeyDown={handleKeyDown}
              hasError={shouldShowError("tenantExistingId") || !!tenantValidationError}
              errorMessage={tenantValidationError || formState.errors.tenantExistingId}
              disabled={isFormDisabled}
              placeholder={t(
                "create-new-entity.tenant-existing-id-placeholder",
                "Paste tenant UUID"
              )}
              required
            />
            {isValidating && (
              <span data-testid="tenant-validating" className="tenant-validating">{t("create-new-entity.tenant-validating", "Validating tenant...")}</span>
            )}
            {validatedTenant && (
              <span data-testid="tenant-validated" className="tenant-validated-success">
                {t("create-new-entity.tenant-validated", "Tenant: {{name}}", {name: validatedTenant.name})}
              </span>
            )}
          </div>
        )}
      </div>

      <div className="form-section">
        <h3 className="section-title">
          {t("create-new-entity.organization-info", "Organization Information")}
        </h3>

        <div className="form-row">
          <TextInput
            id="dba-name-input"
            data-testid="dba-name-input"
            name="dbaName"
            label={t("create-new-entity.dba-name", "DBA Name")}
            value={formState.data.dbaName}
            onChange={handleInputChange("dbaName")}
            onKeyDown={handleKeyDown}
            hasError={shouldShowError("dbaName")}
            errorMessage={formState.errors.dbaName}
            disabled={isFormDisabled}
            required
          />
        </div>

        <div className="form-row">
          <TextInput
            id="legal-name-input"
            data-testid="legal-name-input"
            name="legalName"
            label={t("create-new-entity.legal-name", "Legal Name")}
            value={formState.data.legalName}
            onChange={handleInputChange("legalName")}
            onKeyDown={handleKeyDown}
            hasError={shouldShowError("legalName")}
            errorMessage={formState.errors.legalName}
            disabled={isFormDisabled}
          />
        </div>

        <div className="form-row">
          <SelectInput
            id="status-select-input"
            data-testid="status-select-input"
            name="status"
            label={t("create-new-entity.status", "Status")}
            value={formState.data.status}
            onChange={handleInputChange("status")}
            disabled={isFormDisabled}
            options={[
              {label: "In Business", value: DealerStatus.IN_BUSINESS},
              {label: "Out of Business", value: DealerStatus.OUT_OF_BUSINESS}
            ]}
            displayDeselectOption={false}
          />
        </div>

        <div className="form-row">
          <TextInput
            id="telecom-numbers-input"
            data-testid="telecom-numbers-input"
            name="telecomNumbers"
            label={t("create-new-entity.telecom-numbers", "Telecom Numbers")}
            value={formState.data.telecomNumbers}
            onChange={handleInputChange("telecomNumbers")}
            onKeyDown={handleKeyDown}
            hasError={shouldShowError("telecomNumbers")}
            errorMessage={formState.errors.telecomNumbers}
            disabled={isFormDisabled}
            placeholder={t(
              "create-new-entity.telecom-numbers-placeholder",
              "Enter comma-separated phone numbers"
            )}
          />
        </div>
      </div>

      <div className="form-section">
        <h3 className="section-title">
          {t("create-new-entity.address-info", "Address Information")}
        </h3>

        <div className="form-row">
          <TextInput
            id="street-address-line1-input"
            data-testid="street-address-line1-input"
            name="streetAddressLine1"
            label={t(
              "create-new-entity.street-address-line1",
              "Street Address Line 1"
            )}
            value={formState.data.streetAddressLine1}
            onChange={handleInputChange("streetAddressLine1")}
            onKeyDown={handleKeyDown}
            hasError={shouldShowError("streetAddressLine1")}
            errorMessage={formState.errors.streetAddressLine1}
            disabled={isFormDisabled}
          />
        </div>

        <div className="form-row">
          <TextInput
            id="street-address-line2-input"
            data-testid="street-address-line2-input"
            name="streetAddressLine2"
            label={t(
              "create-new-entity.street-address-line2",
              "Street Address Line 2"
            )}
            value={formState.data.streetAddressLine2}
            onChange={handleInputChange("streetAddressLine2")}
            onKeyDown={handleKeyDown}
            hasError={shouldShowError("streetAddressLine2")}
            errorMessage={formState.errors.streetAddressLine2}
            disabled={isFormDisabled}
          />
        </div>

        <div className="form-row-group">
          <div className="form-row form-row--half">
            <TextInput
              id="city-input"
              data-testid="city-input"
              name="city"
              label={t("create-new-entity.city", "City")}
              value={formState.data.city}
              onChange={handleInputChange("city")}
              onKeyDown={handleKeyDown}
              hasError={shouldShowError("city")}
              errorMessage={formState.errors.city}
              disabled={isFormDisabled}
            />
          </div>

          <div className="form-row form-row--half">
            <TextInput
              id="state-input"
              data-testid="state-input"
              name="state"
              label={t("create-new-entity.state", "State")}
              value={formState.data.state}
              onChange={handleInputChange("state")}
              onKeyDown={handleKeyDown}
              hasError={shouldShowError("state")}
              errorMessage={formState.errors.state}
              disabled={isFormDisabled}
            />
          </div>
        </div>

        <div className="form-row-group">
          <div className="form-row form-row--half">
            <TextInput
              id="country-input"
              data-testid="country-input"
              name="country"
              label={t("create-new-entity.country", "Country")}
              value={formState.data.country}
              onChange={handleInputChange("country")}
              onKeyDown={handleKeyDown}
              hasError={shouldShowError("country")}
              errorMessage={formState.errors.country}
              disabled={isFormDisabled}
            />
          </div>

          <div className="form-row form-row--half">
            <TextInput
              id="zip-input"
              data-testid="zip-input"
              name="zip"
              label={t("create-new-entity.zip", "ZIP Code")}
              value={formState.data.zip}
              onChange={handleInputChange("zip")}
              onKeyDown={handleKeyDown}
              hasError={shouldShowError("zip")}
              errorMessage={formState.errors.zip}
              disabled={isFormDisabled}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
